import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@cobliteam/ui/components';
import Button from '@cobliteam/ui/components/Button';
import Card, { CardContent } from '@cobliteam/ui/components/Card';
import Dialog from '@cobliteam/ui/components/Dialog';
import Skeleton from '@cobliteam/ui/components/Skeleton';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { saveAs } from 'file-saver';
import { useState } from 'react';
import type { FileS3Data } from '../../types';

interface FileItemProps {
  file: FileS3Data['data'][number];
}

const getFileIcon = (contentType?: string, fileName?: string) => {
  const extension = fileName?.split('.').pop()?.toLowerCase() || '';
  const mimeType = contentType?.toLowerCase() || '';

  // Image files
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico', 'tiff', 'tif'].includes(extension) || mimeType.startsWith('image/')) {
    return <Icon name='image' size='lg' className='text-gray-10' />;
  }

  // PDF files
  if (extension === 'pdf' || mimeType === 'application/pdf') {
    return <Icon name='file-text' size='lg' className='text-gray-10' />;
  }

  // Excel/Spreadsheet files
  if (['xls', 'xlsx', 'xlsm', 'xlsb'].includes(extension) || mimeType.includes('spreadsheet') || mimeType.includes('excel')) {
    return <Icon name='file-chart-column' size='lg' className='text-gray-10' />;
  }

  // CSV files
  if (extension === 'csv' || mimeType === 'text/csv' || mimeType === 'application/csv') {
    return <Icon name='file-chart-column' size='lg' className='text-gray-10' />;
  }

  // Word/Document files
  if (['doc', 'docx', 'odt', 'rtf'].includes(extension) || mimeType.includes('word') || mimeType.includes('document')) {
    return <Icon name='file-text' size='lg' className='text-gray-10' />;
  }

  // Video files
  if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv', 'm4v', '3gp'].includes(extension) || mimeType.startsWith('video/')) {
    return <Icon name='video' size='lg' className='text-gray-10' />;
  }

  // Audio files
  if (['mp3', 'wav', 'flac', 'aac', 'ogg', 'wma', 'm4a'].includes(extension) || mimeType.startsWith('audio/')) {
    return <Icon name='music' size='lg' className='text-gray-10' />;
  }

  // Archive files
  if (['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz'].includes(extension) || mimeType.includes('zip') || mimeType.includes('archive') || mimeType.includes('compressed')) {
    return <Icon name='archive' size='lg' className='text-gray-10' />;
  }

  // Code files
  if (
    ['js', 'ts', 'jsx', 'tsx', 'py', 'java', 'cpp', 'c', 'h', 'cs', 'php', 'rb', 'go', 'rs', 'swift', 'kt'].includes(extension) ||
    mimeType.includes('javascript') ||
    mimeType.includes('typescript') ||
    mimeType.includes('text/x-')
  ) {
    return <Icon name='file-code' size='lg' className='text-gray-10' />;
  }

  // JSON/XML files
  if (['json', 'xml', 'yaml', 'yml', 'toml'].includes(extension) || mimeType.includes('json') || mimeType.includes('xml')) {
    return <Icon name='file-code' size='lg' className='text-gray-10' />;
  }

  // HTML files
  if (['html', 'htm'].includes(extension) || mimeType === 'text/html') {
    return <Icon name='file-code' size='lg' className='text-gray-10' />;
  }

  // Text files
  if (extension === 'txt' || mimeType === 'text/plain') {
    return <Icon name='file-text' size='lg' className='text-gray-10' />;
  }

  // PowerPoint files
  if (['ppt', 'pptx', 'odp'].includes(extension) || mimeType.includes('presentation')) {
    return <Icon name='file-chart-column' size='lg' className='text-gray-10' />;
  }

  // Default file icon
  return <Icon name='file' size='lg' className='text-gray-10' />;
};

const isImageFile = (file: FileS3Data['data'][number]) => {
  const extension = file.filename?.split('.').pop()?.toLowerCase() || '';
  return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(extension) || file.contentType?.startsWith('image/');
};

const isHtmlFile = (file: FileS3Data['data'][number]) => {
  const extension = file.filename?.split('.').pop()?.toLowerCase() || '';
  return ['html', 'htm'].includes(extension) || file.contentType === 'text/html';
};

const isPdfFile = (file: FileS3Data['data'][number]) => {
  const extension = file.filename?.split('.').pop()?.toLowerCase() || '';
  return extension === 'pdf' || file.contentType === 'application/pdf';
};

const FileItem = ({ file }: FileItemProps) => {
  const { filename, s3Url, contentType } = file;
  const displayFileName = filename || 'File';

  const handleDownload = () => {
    if (s3Url) {
      saveAs(s3Url, displayFileName);
    }
  };

  return (
    <Card className='mt-4 border-gray-4'>
      <CardContent className='p-3'>
        <div className='flex items-center gap-4'>
          {/* File Icon */}
          <div className='flex size-14 flex-shrink-0 items-center justify-center rounded-md border border-gray-4 bg-gray-2'>{getFileIcon(contentType, displayFileName)}</div>

          {/* File Info */}
          <div className='flex flex-col gap-2'>
            <Typography variant='text-md-semibold' className='text-gray-12'>
              {displayFileName}
            </Typography>
            <Button variant='outlined' color='primary' size='sm' startIcon={<Icon name='download' size='sm' />} onClick={handleDownload}>
              Download
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ImageItem = ({ file }: FileItemProps) => {
  const [imageError, setImageError] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { filename, s3Url } = file;
  const displayFileName = filename || 'Image';

  const handleDownload = () => {
    if (s3Url) {
      saveAs(s3Url, displayFileName);
    }
  };

  const handleOpenNewTab = () => {
    if (s3Url) {
      window.open(s3Url, '_blank');
    }
  };

  if (imageError || !s3Url) {
    return <FileItem file={file} />;
  }

  return (
    <>
      <Card className='my-2 w-fit'>
        <CardContent className='w-fit p-2'>
          <div className='group relative'>
            {/* Image Preview */}
            <div className='cursor-pointer overflow-hidden rounded-lg' onClick={() => setIsDialogOpen(true)}>
              <img
                src={s3Url}
                alt={displayFileName}
                className='max-h-72 max-w-full object-contain transition-transform duration-200 group-hover:scale-[1.02]'
                onError={() => setImageError(true)}
              />
            </div>

            {/* Actions - Top Right Overlay */}
            <div className='absolute top-2 right-2 flex gap-0.5 rounded bg-black/60 p-0.5 opacity-0 backdrop-blur-sm transition-opacity duration-200 group-hover:opacity-100'>
              <Tooltip content='Ver em tela cheia' asChild>
                <button type='button' className='rounded p-2 transition-colors hover:bg-white/20' onClick={() => setIsDialogOpen(true)}>
                  <Icon name='maximize-2' size='xs' className='text-white' />
                </button>
              </Tooltip>
              <Tooltip content='Download' asChild>
                <button type='button' className='rounded p-2 transition-colors hover:bg-white/20' onClick={handleDownload}>
                  <Icon name='download' size='xs' className='text-white' />
                </button>
              </Tooltip>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Full-size Image Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <Dialog.Content className='max-w-[90vw]' size='lg'>
          <Dialog.Header>
            <Dialog.Title>{displayFileName}</Dialog.Title>
            <Dialog.Description className='sr-only'>Visualização da imagem em tamanho maior</Dialog.Description>
            <Dialog.Close />
          </Dialog.Header>
          <Dialog.Body>
            <div className='flex justify-center'>
              <img src={s3Url} alt={displayFileName} className='max-h-[70vh] max-w-full rounded-lg object-contain' />
            </div>
          </Dialog.Body>
          <Dialog.Footer>
            <div className='flex justify-end gap-2'>
              <Button variant='outlined' color='default' size='sm' startIcon={<Icon name='external-link' size='sm' />} onClick={handleOpenNewTab}>
                Abrir em nova aba
              </Button>
              <Button variant='contained' color='primary' size='sm' startIcon={<Icon name='download' size='sm' />} onClick={handleDownload}>
                Download
              </Button>
            </div>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </>
  );
};

const HTMLItem = ({ file }: FileItemProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { filename, s3Url } = file;
  const displayFileName = filename || 'HTML File';

  const handleDownload = () => {
    if (s3Url) {
      saveAs(s3Url, displayFileName);
    }
  };

  const handleOpenNewTab = () => {
    if (s3Url) {
      window.open(s3Url, '_blank');
    }
  };

  const handleShare = () => {
    if (s3Url) {
      window.open(s3Url, '_blank');
    }
  };

  if (!s3Url) {
    return <FileItem file={file} />;
  }

  return (
    <>
      <Card className='not-prose mt-4 w-full overflow-hidden border-gray-4 p-0 shadow-sm'>
        <Accordion type='single' collapsible className='w-full' defaultValue={undefined}>
          <AccordionItem value='html' className='border-0'>
            <AccordionTrigger className='group flex w-full items-center justify-between gap-4 border-gray-4 border-b px-4 py-3 transition-all hover:bg-gray-2 focus:outline-none focus:ring-0 [&[data-state=open]]:bg-gray-2'>
              <div className='flex items-center gap-3'>
                <div className='flex size-8 shrink-0 items-center justify-center rounded-md bg-gray-3 text-gray-10 transition-colors group-hover:bg-gray-4'>
                  <Icon name='file-code' size='sm' />
                </div>
                <div className='flex flex-col items-start gap-0.5'>
                  <Typography variant='text-md-semibold' className='text-gray-12'>
                    Dashboard Interativo
                  </Typography>
                  <Typography variant='text-sm' className='text-gray-9'>
                    Página interativa com o dashboard
                  </Typography>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className='data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2 p-0 focus:outline-none focus:ring-0 data-[state=closed]:animate-out data-[state=open]:animate-in'>
              <div className='group relative p-4'>
                {/* HTML Preview */}
                <div className='overflow-hidden rounded-lg border border-gray-4'>
                  <iframe src={s3Url} title={displayFileName} className='h-96 w-full border-0' sandbox='allow-scripts allow-same-origin' />
                </div>

                {/* Actions */}
                <div className='mt-2 flex justify-end gap-2'>
                  <Button variant='outlined' color='default' size='sm' startIcon={<Icon name='external-link' size='sm' />} onClick={handleOpenNewTab}>
                    Abrir em nova aba
                  </Button>
                  <Button variant='contained' color='primary' size='sm' startIcon={<Icon name='download' size='sm' />} onClick={handleDownload}>
                    Download
                  </Button>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </Card>

      {/* Full-size HTML Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <Dialog.Content className='max-w-[90vw]' size='lg'>
          <Dialog.Body className='p-0'>
            <div className='flex justify-center'>
              <iframe src={s3Url} title={displayFileName} className='h-[70vh] w-full rounded-lg border border-gray-4' sandbox='allow-scripts allow-same-origin' />
            </div>
          </Dialog.Body>
          <Dialog.Footer>
            <div className='flex justify-end gap-2'>
              <Button variant='outlined' color='default' size='sm' startIcon={<Icon name='link' size='sm' />} onClick={handleShare}>
                Compartilhar
              </Button>
              <Button variant='outlined' color='default' size='sm' startIcon={<Icon name='external-link' size='sm' />} onClick={handleOpenNewTab}>
                Abrir em nova aba
              </Button>
              <Button variant='contained' color='primary' size='sm' startIcon={<Icon name='download' size='sm' />} onClick={handleDownload}>
                Download
              </Button>
            </div>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </>
  );
};

const PDFItem = ({ file }: FileItemProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { filename, s3Url } = file;
  const displayFileName = filename || 'PDF File';

  const handleDownload = () => {
    if (s3Url) {
      saveAs(s3Url, displayFileName);
    }
  };

  const handleOpenNewTab = () => {
    if (s3Url) {
      window.open(s3Url, '_blank');
    }
  };

  const handleShare = () => {
    if (s3Url) {
      window.open(s3Url, '_blank');
    }
  };

  if (!s3Url) {
    return <FileItem file={file} />;
  }

  return (
    <>
      <Card className='my-2 w-full'>
        <CardContent className='p-2'>
          <div className='group relative'>
            {/* PDF Preview */}
            <div className='cursor-pointer overflow-hidden rounded-lg border border-gray-4' onClick={() => setIsDialogOpen(true)}>
              <iframe src={`${s3Url}#toolbar=0`} title={displayFileName} className='h-72 w-full border-0 transition-transform duration-200 group-hover:scale-[1.01]' />
            </div>

            {/* Actions - Top Right Overlay */}
            <div className='absolute top-2 right-2 flex gap-0.5 rounded bg-black/60 p-0.5 opacity-0 backdrop-blur-sm transition-opacity duration-200 group-hover:opacity-100'>
              <button type='button' className='rounded p-1 transition-colors hover:bg-white/20' onClick={() => setIsDialogOpen(true)}>
                <Icon name='maximize-2' size='xs' className='text-white' />
              </button>
              <button type='button' className='rounded p-1 transition-colors hover:bg-white/20' onClick={handleShare}>
                <Icon name='link' size='xs' className='text-white' />
              </button>
              <button type='button' className='rounded p-1 transition-colors hover:bg-white/20' onClick={handleDownload}>
                <Icon name='download' size='xs' className='text-white' />
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Full-size PDF Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <Dialog.Content className='max-w-[90vw]' size='lg'>
          <Dialog.Header>
            <Dialog.Title>{displayFileName}</Dialog.Title>
            <Dialog.Description className='sr-only'>Visualização do arquivo PDF em tamanho maior</Dialog.Description>
            <Dialog.Close />
          </Dialog.Header>
          <Dialog.Body>
            <div className='flex justify-center'>
              <iframe src={`${s3Url}#toolbar=1`} title={displayFileName} className='h-[70vh] w-full rounded-lg border border-gray-4' />
            </div>
          </Dialog.Body>
          <Dialog.Footer>
            <div className='flex justify-end gap-2'>
              <Button variant='outlined' color='default' size='sm' startIcon={<Icon name='link' size='sm' />} onClick={handleShare}>
                Compartilhar
              </Button>
              <Button variant='outlined' color='default' size='sm' startIcon={<Icon name='external-link' size='sm' />} onClick={handleOpenNewTab}>
                Abrir em nova aba
              </Button>
              <Button variant='contained' color='primary' size='sm' startIcon={<Icon name='download' size='sm' />} onClick={handleDownload}>
                Download
              </Button>
            </div>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </>
  );
};

const FileItemSkeleton = () => (
  <Card className='mt-4 border-gray-4'>
    <CardContent className='p-3'>
      <div className='flex items-center gap-4'>
        {/* File Icon Skeleton */}
        <Skeleton className='size-14 shrink-0 rounded-md' />
        {/* File Info Skeleton */}
        <div className='flex flex-col gap-2'>
          <Skeleton className='h-5 w-40' />
          <Skeleton className='h-8 w-24 rounded-md' />
        </div>
      </div>
    </CardContent>
  </Card>
);

export const FileSkeleton = () => (
  <div className='grid grid-cols-2 gap-2'>
    <FileItemSkeleton />
  </div>
);

const File = ({ files }: { files: FileS3Data['data'] }) => {
  return (
    <div className={cn('grid grid-cols-2 gap-2', files.length > 1 ? 'grid-cols-2' : 'grid-cols-1')}>
      {files.map((file, index) => {
        if (isPdfFile(file)) {
          return <PDFItem key={`${file.filename}-${index}`} file={file} />;
        }
        if (isHtmlFile(file)) {
          return <HTMLItem key={`${file.filename}-${index}`} file={file} />;
        }
        if (isImageFile(file)) {
          return <ImageItem key={`${file.filename}-${index}`} file={file} />;
        }
        return <FileItem key={`${file.filename}-${index}`} file={file} />;
      })}
    </div>
  );
};

export default File;
